/**
 * Shared workspace shell — one arrangement + one TopBar for Flow and Block.
 *
 * Ownership tokens prevent inactive-surface cleanup from clearing TopBar.
 * Report effects depend only on primitive fields (not the report object / shell
 * context identity) to avoid "Maximum update depth exceeded" loops.
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import type { Document } from "@/lib/api";
import type { AutoSaveStatus } from "@/hooks/useAutoSave";
import type { EditorMode } from "@/components/editor/TopBar";
import type { UserPresence } from "@/hooks/usePresence";
import {
  useChatFirstLayout,
  type EditorArrangement,
} from "@/hooks/useChatFirstLayout";

export type WorkspaceTopBarReport = {
  title: string;
  onTitleChange: (title: string) => void;
  mode?: EditorMode;
  onModeChange?: (mode: EditorMode) => void;
  saveStatus: AutoSaveStatus;
  isDirty: boolean;
  onSaveNow: () => void;
  lastSavedAt?: Date | null;
  isPublic?: boolean;
  shareLink?: string;
  shareSlug?: string;
  ownerId?: string;
  presenceUsers?: UserPresence[];
  onPasteLatex?: () => void;
  validationIssueCount?: number;
  onOpenPackages?: () => void;
  onOpenSettings?: () => void;
  document?: Document;
  documentLoading?: boolean;
  documentReadOnly?: boolean;
};

export type WorkspaceShellContextValue = {
  documentId: string;
  arrangement: EditorArrangement;
  setArrangement: (a: EditorArrangement) => void;
  chatLayout: ReturnType<typeof useChatFirstLayout>;
  topBar: WorkspaceTopBarReport | null;
  reportTopBar: (report: WorkspaceTopBarReport | null, owner: symbol) => void;
  openPalette: () => void;
  paletteEpoch: number;
};

const WorkspaceShellContext = createContext<WorkspaceShellContextValue | null>(null);

export function useWorkspaceShell(): WorkspaceShellContextValue {
  const ctx = useContext(WorkspaceShellContext);
  if (!ctx) throw new Error("useWorkspaceShell requires WorkspaceShellProvider");
  return ctx;
}

export function useWorkspaceShellOptional(): WorkspaceShellContextValue | null {
  return useContext(WorkspaceShellContext);
}

function topBarContentEqual(
  a: WorkspaceTopBarReport | null,
  b: WorkspaceTopBarReport | null,
): boolean {
  if (a === b) return true;
  if (!a || !b) return false;
  return (
    a.title === b.title &&
    a.saveStatus === b.saveStatus &&
    a.isDirty === b.isDirty &&
    a.validationIssueCount === b.validationIssueCount &&
    a.documentLoading === b.documentLoading &&
    a.documentReadOnly === b.documentReadOnly &&
    a.document?.id === b.document?.id &&
    a.document?.updatedAt === b.document?.updatedAt &&
    a.mode === b.mode &&
    a.isPublic === b.isPublic &&
    a.shareLink === b.shareLink &&
    a.shareSlug === b.shareSlug &&
    a.ownerId === b.ownerId &&
    (a.lastSavedAt?.getTime() ?? 0) === (b.lastSavedAt?.getTime() ?? 0)
  );
}

/**
 * Active surface reports TopBar fields.
 * Uses a ref for the report payload so effect deps stay primitive-stable.
 * Does NOT depend on shell context identity (would loop when topBar updates).
 */
export function useReportWorkspaceTopBar(
  active: boolean,
  report: WorkspaceTopBarReport | null,
) {
  const shell = useWorkspaceShellOptional();
  const owner = useRef(Symbol("topbar-owner")).current;
  const reportRef = useRef(report);
  reportRef.current = report;

  // Stable callback via ref — never put `shell` object in effect deps.
  const reportTopBarRef = useRef(shell?.reportTopBar);
  reportTopBarRef.current = shell?.reportTopBar;
  const hasShell = !!shell;

  const title = report?.title ?? "";
  const saveStatus = report?.saveStatus ?? "idle";
  const isDirty = report?.isDirty ?? false;
  const lastSavedAt = report?.lastSavedAt?.getTime() ?? 0;
  const validationIssueCount = report?.validationIssueCount ?? 0;
  const documentLoading = report?.documentLoading ?? false;
  const docId = report?.document?.id ?? "";
  const docTitle = report?.document?.title ?? "";
  const docUpdated = report?.document?.updatedAt ?? "";
  const isPublic = report?.isPublic ?? false;
  const shareLink = report?.shareLink ?? "";
  const shareSlug = report?.shareSlug ?? "";
  const ownerId = report?.ownerId ?? "";
  const mode = report?.mode ?? "visual";

  useEffect(() => {
    const reportFn = reportTopBarRef.current;
    if (!hasShell || !reportFn) return;
    if (!active || !reportRef.current) return;
    reportFn(reportRef.current, owner);
    return () => {
      reportFn(null, owner);
    };
  }, [
    hasShell,
    active,
    owner,
    title,
    saveStatus,
    isDirty,
    lastSavedAt,
    validationIssueCount,
    documentLoading,
    docId,
    docTitle,
    docUpdated,
    isPublic,
    shareLink,
    shareSlug,
    ownerId,
    mode,
  ]);
}

export function WorkspaceShellProvider({
  documentId,
  children,
}: {
  documentId: string;
  children: ReactNode;
}) {
  const chatLayout = useChatFirstLayout();
  const [topBar, setTopBar] = useState<WorkspaceTopBarReport | null>(null);
  const ownerRef = useRef<symbol | null>(null);
  const [paletteEpoch, setPaletteEpoch] = useState(0);

  const reportTopBar = useCallback((report: WorkspaceTopBarReport | null, owner: symbol) => {
    if (report) {
      ownerRef.current = owner;
      setTopBar((prev) => {
        // Must return `prev` on equality — a new object always re-renders
        // and can re-trigger reporters (max update depth).
        if (topBarContentEqual(prev, report)) {
          // Keep latest handlers without identity change (stale closures otherwise).
          if (prev) {
            prev.onTitleChange = report.onTitleChange;
            prev.onModeChange = report.onModeChange;
            prev.onSaveNow = report.onSaveNow;
            prev.onPasteLatex = report.onPasteLatex;
            prev.onOpenPackages = report.onOpenPackages;
            prev.onOpenSettings = report.onOpenSettings;
            prev.presenceUsers = report.presenceUsers;
            prev.document = report.document;
          }
          return prev;
        }
        return report;
      });
      return;
    }
    if (ownerRef.current === owner) {
      ownerRef.current = null;
      setTopBar(null);
    }
  }, []);

  const openPalette = useCallback(() => {
    setPaletteEpoch((n) => n + 1);
    window.dispatchEvent(new CustomEvent("lilia:workspace-open-palette"));
  }, []);


  // Stabilize chatLayout identity for context consumers (hook returns new
  // object every render; only re-emit when fields consumers read change).
  const chatLayoutMemo = useMemo(
    () => chatLayout,
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional field deps
    [
      chatLayout.layout,
      chatLayout.isChatLayout,
      chatLayout.isSideLayout,
      chatLayout.artifact,
      chatLayout.artifactOpen,
      chatLayout.artifactWidth,
      chatLayout.maxArtifactWidth,
      chatLayout.setLayout,
      chatLayout.openArtifact,
      chatLayout.closeArtifact,
      chatLayout.setArtifactWidth,
      chatLayout.onArtifactResize,
      // The pane remembers a width per arrangement, so the slot is part of what
      // consumers read — without it they keep an object whose artifactWidth
      // belongs to the arrangement they just left.
      chatLayout.widthSlot,
      chatLayout.setWidthSlot,
    ],
  );

  const value = useMemo(
    () => ({
      documentId,
      arrangement: chatLayout.layout,
      setArrangement: chatLayout.setLayout,
      chatLayout: chatLayoutMemo,
      topBar,
      reportTopBar,
      openPalette,
      paletteEpoch,
    }),
    [
      documentId,
      chatLayout.layout,
      chatLayout.setLayout,
      chatLayoutMemo,
      topBar,
      reportTopBar,
      openPalette,
      paletteEpoch,
    ],
  );

  return (
    <WorkspaceShellContext.Provider value={value}>
      {children}
    </WorkspaceShellContext.Provider>
  );
}
